import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, ArrowLeft } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { DEFAULT_PRIVACY } from '../lib/privacyDefault';

function parsePolicy(text) {
  const lines = String(text || '').replace(/\r/g, '').split('\n');
  const blocks = [];
  let list = null;
  const flush = () => { if (list) { blocks.push({ type: 'ul', items: list }); list = null; } };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flush(); continue; }
    if (line.startsWith('# ')) { flush(); blocks.push({ type: 'h', text: line.slice(2).trim() }); }
    else if (line.startsWith('- ') || line.startsWith('* ')) { if (!list) list = []; list.push(line.slice(2).trim()); }
    else { flush(); blocks.push({ type: 'p', text: line }); }
  }
  flush();
  return blocks;
}

const PrivacyPolicy = () => {
  const { settings } = useApp();
  const email = settings.email || 'info@gobracapital.ae';
  const phone = settings.phone || '';
  const address = settings.address || 'Dubai, United Arab Emirates';
  const rawWebsite = (settings.website || 'www.gobracapital.ae').trim();
  const websiteLabel = rawWebsite.replace(/^https?:\/\//i, '').replace(/\/+$/, '');
  const websiteHref = /^https?:\/\//i.test(rawWebsite) ? rawWebsite : `https://${rawWebsite}`;
  const company = settings.name || 'GOBRA Capital Real Estate';
  const blocks = parsePolicy(settings.privacyPolicy || DEFAULT_PRIVACY);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `Privacy Policy | ${company}`;
  }, [company]);

  return (
    <main className="bg-gobra-cream min-h-screen" data-testid="privacy-policy-page">
      <section className="bg-gobra-slate text-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-16 md:py-20">
          <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-gobra-orange transition-colors text-sm mb-6" data-testid="privacy-back-home">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
              <ShieldCheck className="h-6 w-6 text-gobra-orange" />
            </div>
            <div>
              <h1 className="font-title text-4xl sm:text-5xl font-bold">Privacy Policy</h1>
              <p className="text-white/50 text-sm mt-1">How we collect, use and protect your information</p>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="space-y-5" data-testid="privacy-body">
          {blocks.map((b, i) => {
            if (b.type === 'h') return <h2 key={i} className="font-title text-lg md:text-xl font-semibold text-gobra-slate pt-5">{b.text}</h2>;
            if (b.type === 'ul') return (
              <ul key={i} className="list-disc pl-5 space-y-2 text-base text-gobra-slate/80 leading-relaxed">
                {b.items.map((it, j) => <li key={j}>{it}</li>)}
              </ul>
            );
            return <p key={i} className="text-base text-gobra-slate/80 leading-relaxed">{b.text}</p>;
          })}

          <h2 className="font-title text-lg md:text-xl font-semibold text-gobra-slate pt-5">Contact Us</h2>
          <p className="text-base text-gobra-slate/80 leading-relaxed">If you have any questions about this Privacy Policy or how your information is handled, please contact us:</p>
          <div className="rounded-xl bg-white border border-gobra-sand p-5 text-base text-gobra-slate/80 space-y-1">
            <p className="font-semibold text-gobra-slate">{company}</p>
            <p>{address}</p>
            {phone && <p>Phone: {phone}</p>}
            <p>Email: <a href={`mailto:${email}`} className="text-gobra-terracotta hover:underline">{email}</a></p>
            <p>Website: <a href={websiteHref} target="_blank" rel="noopener noreferrer" className="text-gobra-terracotta hover:underline">{websiteLabel}</a></p>
          </div>
        </div>
      </section>
    </main>
  );
};

export default PrivacyPolicy;
